/* eslint-disable eqeqeq */
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API, fileUpload, taxOptions } from "../../config";
import { Button } from "../../components/ui/button";
import CustomLabelText from "../../components/CustomLabelText";
import { Label } from "../../components/ui/label";

import { Loader2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { getSubAssetDetail } from "./SubAssetHelper";
import Spin from "../../components/Spin";
import { AddSubAssetSchema } from "../../lib/Validation";
import { AssetList, VendorList } from "../../lib/OptionsAPI";
import Editor from "../../components/Editor";
import VendorSelect from "../../components/VendorSelect";
import AssetSelect from "../../components/AssetSelect";
import {
  formatSubcontractorName,
  getModuleNamingBySlug,
  MaxLimit,
} from "../../lib/Misc";
import { NumericFormat } from "react-number-format";
import { cn } from "../../lib/utils";
import SearchSelect from "../../components/SearchSelect";
import { useOrganization } from "../../lib/context/OrganisationContext";

const AddEditSubAsset = ({
  edit,
  subAssetID,
  toggleShowAddEditSubAsset,
  fetchSubAssets,
  trustId,
}) => {
  const { id } = useParams();
  const { organizationData } = useOrganization();

  const [subAssetData, setSubAssetData] = useState();
  const [loading, setLoading] = useState(false);
  const subAssetId = subAssetID ? subAssetID : id;

  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorListing, setVendorListing] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [vendorId, setVendorId] = useState();

  const [assetLoading, setAssetLoading] = useState(false);
  const [assetListing, setAssetListing] = useState([]);
  const [assetList, setAssetList] = useState([]);
  const [assetId, setAssetId] = useState();

  const [descriptionValue, setDescriptionValue] = useState("");

  async function fetchData() {
    try {
      setLoading(true);
      const data = await getSubAssetDetail(subAssetId);
      if (data?.vendor_id) {
        FetchVendorList();
        setVendorId(data?.vendor_id);
        if (data?.vendor) {
          setVendorList([
            data?.vendor
              ? {
                  ...data?.vendor,

                  value: data?.vendor?.id,
                  label: `${formatSubcontractorName(data?.vendor)}${
                    data?.vendor?.code ? ` (${data?.vendor?.code})` : ""
                  }`,
                }
              : {},
          ]);
        }
      }
      if (data?.asset_id) {
        FetchAssetList();
        setAssetId(data?.asset_id);
        if (data?.asset) {
          setAssetList([
            data?.asset
              ? {
                  ...data?.asset,

                  value: data?.asset?.id,
                  label: data?.asset?.name
                    ? `${data?.asset?.name}${
                        data?.asset?.code ? ` (${data?.asset?.code})` : ""
                      }`
                    : "",
                }
              : {},
          ]);
        }
      }
      if (data?.description) {
        setDescriptionValue(data?.description);
      }
      setSubAssetData(data);
      setLoading(false);
    } catch (e) {
      setSubAssetData(null);
    }
  }

  useEffect(() => {
    if (edit) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subAssetId]);

  // Submit Form
  const handleFormSubmit = async (value) => {
    value.estimated_value = Number(
      `${value?.estimated_value}`.replace(/,/g, "")
    );
    const formData = new FormData();
    formData.append("trust_id", trustId);
    formData.append("vendor_id", value?.vendor_id ? value?.vendor_id : "");
    formData.append("asset_id", value?.asset_id ? value?.asset_id : "");
    formData.append("name", value?.name ? value?.name : "");
    formData.append("identifier", value?.identifier ? value?.identifier : "");
    formData.append("description", descriptionValue ? descriptionValue : "");
    formData.append(
      "estimated_value",
      value?.estimated_value ? value?.estimated_value : ""
    );
    formData.append("tax_type", value?.tax_type ? value?.tax_type : 0);
    formData.append(
      "tax_percentage",
      value?.tax_percentage
        ? value?.tax_percentage?.toString()?.replace("%", "")
        : "0"
    );
    if (subAssetId) {
      formData.append("_method", "put");
    }
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.SUB_ASSETS_ADD}${subAssetId ? `/${subAssetId}` : ""}`,
        formData
      );
      setLoading(false);
      if (data) {
        setDescriptionValue("");
        toggleShowAddEditSubAsset();
        fetchSubAssets && fetchSubAssets();
      }
      return data?.data;
    } catch (error) {
      setSubAssetData(value);
      setLoading(false);
    }
  };

  const FetchVendorList = async () => {
    await VendorList(
      organizationData,
      trustId,
      1,
      setVendorLoading,
      setVendorList,
      true
    );
  };

  const FetchAssetList = async () => {
    await AssetList(
      organizationData,
      trustId,
      vendorId,
      1,
      setAssetLoading,
      setAssetList,
      true
    );
  };

  useEffect(() => {
    if (vendorList) {
      let vendorTempListing = vendorList?.map((item) => ({
        ...item,
        value: item?.id,
        label: `${formatSubcontractorName(item)}${
          item?.code ? ` (${item?.code})` : ""
        }`,
      }));
      setVendorListing(vendorTempListing || []);
    } else {
      setVendorListing([]);
    }
  }, [vendorList]);

  useEffect(() => {
    if (assetList) {
      let assetTempListing = assetList?.map((item) => ({
        ...item,
        value: item?.id,
        label: item?.name
          ? `${item.name}${item?.code ? ` (${item?.code})` : ""}`
          : "",
      }));
      setAssetListing(assetTempListing || []);
    } else {
      setAssetListing([]);
    }
  }, [assetList]);

  return (
    <Formik
      initialValues={{
        vendor_id: subAssetData?.vendor_id,
        asset_id: subAssetData?.asset_id,
        name: subAssetData?.name,
        description: "",
        identifier: subAssetData?.identifier ? subAssetData?.identifier : "",
        estimated_value: subAssetData?.estimated_value
          ? subAssetData?.estimated_value
          : 0,
        tax_type: subAssetData?.tax_type ? subAssetData?.tax_type : "0",
        tax_percentage:
          subAssetData?.tax_percentage || subAssetData?.tax_percentage == 0
            ? subAssetData?.tax_percentage
            : "0",
      }}
      enableReinitialize
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={AddSubAssetSchema(organizationData)}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        validateField,
        setTouched,
        isValid,
      }) => (
        <>
          {loading && <Spin expand={true} fullscreen={true} />}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-section md:m-0 m-5"
          >
            <div className="form-box overflow-y-auto overflow-x-hidden p-4 border rounded-lg">
              <div className="grid gap-4">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <div className="min-w-0">
                      <Label htmlFor="vendor_id">
                        <CustomLabelText
                          labelText={getModuleNamingBySlug(
                            organizationData?.organizationData,
                            "vendors",
                            "Vendor"
                          )}
                          required={true}
                          bottomAlign={true}
                          errorField={errors?.vendor_id}
                        />
                      </Label>
                      <div className="relative mt-2">
                        <VendorSelect
                          trustId={trustId}
                          frameworkData={vendorListing} // List of vendors
                          optionType={getModuleNamingBySlug(
                            organizationData?.organizationData,
                            "vendors",
                            "Vendor"
                          )}
                          handleSelect={(e) => {
                            setVendorId(e);
                            setFieldValue("vendor_id", e);
                            setAssetListing([]);
                            setAssetList([]);
                            setAssetId();
                            setFieldValue("asset_id", "");
                          }}
                          name="vendor_id"
                          disabled={false}
                          initialValue={vendorId ? vendorId : ""}
                          setVendorList={setVendorList} // Update vendor list as new data loads
                          setVendorLoading={setVendorLoading} // Control loading state
                          onClear={() => {
                            setVendorId();
                            setAssetListing([]);
                            setAssetList([]);
                            setAssetId();
                            setFieldValue("asset_id", "");
                            setFieldValue("vendor_id", "");
                          }}
                          className={
                            errors?.vendor_id &&
                            `focus-visible:ring-red-500 border-red-500`
                          }
                          loading={vendorLoading}
                        />
                        {vendorLoading && (
                          <span className="absolute top-2 right-2 animate-spin">
                            <Loader2 width={20} height={20} />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <div className="min-w-0">
                    <Label htmlFor="asset_id">
                      <CustomLabelText
                        labelText={getModuleNamingBySlug(
                          organizationData?.organizationData,
                          "assets",
                          "Asset"
                        )}
                        required={true}
                        errorField={errors?.asset_id}
                        bottomAlign={true}
                      />
                    </Label>
                    <div className="relative mt-2">
                      <AssetSelect
                        trustId={trustId}
                        vendorId={vendorId ? vendorId : ""}
                        frameworkData={assetListing} // List of vendors
                        optionType={getModuleNamingBySlug(
                          organizationData?.organizationData,
                          "assets",
                          "Asset"
                        )}
                        handleSelect={(e) => {
                          setAssetId(e);
                          setFieldValue("asset_id", e);
                        }}
                        name="asset_id"
                        disabled={!vendorId}
                        initialValue={assetId ? assetId : ""}
                        setAssetList={setAssetList} // Update vendor list as new data loads
                        setAssetLoading={setAssetLoading} // Control loading state
                        onClear={() => {
                          setAssetId();
                          setFieldValue("asset_id", "");
                        }}
                        className={
                          errors?.asset_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        loading={assetLoading}
                      />
                      {assetLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="name">
                      <CustomLabelText
                        required={true}
                        labelText={`${getModuleNamingBySlug(
                          organizationData?.organizationData,
                          "sub-assets",
                          "Sub Asset"
                        )} Name`}
                        bottomAlign={true}
                        errorField={errors?.name}
                      />
                    </Label>
                    <Input
                      id="name"
                      placeholder="Enter Name"
                      required
                      name="name"
                      onChange={(e) => {
                        setFieldValue("name", e?.target?.value);
                      }}
                      onBlur={handleBlur}
                      value={values?.name}
                      className={
                        errors?.name &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="identifier">
                      <CustomLabelText
                        labelText={`${getModuleNamingBySlug(
                          organizationData?.organizationData,
                          "sub-assets",
                          "Sub Asset"
                        )} Identifier`}
                        errorField={errors?.identifier}
                      />
                    </Label>
                    <Input
                      id="identifier"
                      placeholder="Enter Identifier"
                      name="identifier"
                      onChange={(e) => {
                        setFieldValue("identifier", e?.target?.value);
                      }}
                      onBlur={handleBlur}
                      value={values?.identifier}
                      className={
                        errors?.identifier &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="estimated_value">
                      <CustomLabelText
                        required={false}
                        labelText="Estimated/Reserved Value($)"
                        errorField={errors?.estimated_value}
                      />
                    </Label>

                    <NumericFormat
                      type="text"
                      name="estimated_value"
                      value={values?.estimated_value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      decimalScale={2}
                      allowNegative={false}
                      thousandSeparator={true}
                      isAllowed={(values) => MaxLimit(values)}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        e.which === 13 && e.preventDefault();
                      }}
                      className={
                        errors?.estimated_value
                          ? `${cn(
                              "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-webPrimary dark:focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            )} focus-visible:ring-red-500 border-red-500`
                          : cn(
                              "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-webPrimary dark:focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            )
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="tax_type">
                      <CustomLabelText
                        required={true}
                        labelText="GST/VAT Applicable"
                        errorField={errors?.tax_type}
                      />
                    </Label>
                    <div className="relative">
                      <SearchSelect
                        frameworks={taxOptions}
                        optionType={"GST Type"}
                        handleSelect={(e) => {
                          setFieldValue(`tax_type`, e);
                          if (e == "0") {
                            setFieldValue(`tax_percentage`, "0");
                          } else {
                            setFieldValue(`tax_percentage`, "10");
                          }
                          setTimeout(() => {
                            setTouched({ tax_percentage: true });
                            validateField("tax_percentage");
                          }, 0);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.tax_type &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        disabled={false}
                        initialValue={values?.tax_type}
                      />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="tax_percentage">
                      <CustomLabelText
                        required={true}
                        labelText="Tax Percentage (%)"
                        errorField={errors?.tax_percentage}
                      />
                    </Label>

                    <NumericFormat
                      type="text"
                      name={`tax_percentage`}
                      value={values?.tax_percentage}
                      onChange={handleChange}
                      onBlur={(e) => {
                        setFieldValue(`tax_percentage`, e?.target?.value);
                      }}
                      decimalScale={2}
                      allowNegative={false}
                      thousandSeparator={true}
                      suffix="%"
                      isAllowed={(values) => MaxLimit(values)}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        e.which === 13 && e.preventDefault();
                      }}
                      className={
                        errors?.tax_percentage
                          ? `${cn(
                              "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-webPrimary dark:focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            )} focus-visible:ring-red-500 border-red-500`
                          : cn(
                              "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-webPrimary dark:focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            )
                      }
                    />
                  </div>

                  <div className="grid gap-2 col-span-full">
                    <Label htmlFor="description">
                      <CustomLabelText
                        labelText={`${getModuleNamingBySlug(
                          organizationData?.organizationData,
                          "sub-assets",
                          "Sub Asset"
                        )} Details`}
                        errorField={errors?.description}
                      />
                    </Label>
                    <Editor
                      value={descriptionValue}
                      setValue={setDescriptionValue}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
              <Button
                className="reset ml-3"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  toggleShowAddEditSubAsset();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default AddEditSubAsset;
